/**
 * Cloudinary Image Optimization Utility — Web Client
 * ─────────────────────────────────────────────────────
 * Transforms raw Cloudinary URLs to request optimized versions
 * from Cloudinary's CDN. No backend changes needed.
 *
 * All vendor-uploaded images now go through the Dashboard's
 * image pipeline (validate → compress → upload with eager transforms).
 * This utility ensures the CLIENT requests the right size for each context.
 *
 * Instagram-style approach:
 *   - Vendors upload any image
 *   - Dashboard compresses + tells Cloudinary to generate standard sizes
 *   - Clients request the right size via URL transformations
 *   - Result: consistent, fast-loading images everywhere
 */

const CLOUDINARY_UPLOAD_RE = /\/upload\/(v\d+\/)/;

/**
 * Return the original Cloudinary resource URL when a persisted URL contains
 * one or more legacy delivery transformations before the version segment.
 * Non-Cloudinary and already-original URLs pass through unchanged.
 */
export const getOriginalCloudinaryUrl = (url) => {
  if (!url || typeof url !== "string" || !url.includes("res.cloudinary.com")) {
    return url;
  }

  return url.replace(/\/upload\/(?:[^/]+\/)+(v\d+\/)/, "/upload/$1");
};

/**
 * Optimize a Cloudinary image URL for a specific display context.
 *
 * @param {string} url       - Raw Cloudinary URL (from database)
 * @param {object} [options] - Override defaults
 * @param {number} [options.width]   - Target width in CSS pixels
 * @param {number} [options.height]  - Target height (optional, omits if not set)
 * @param {string} [options.crop]    - Crop mode: 'fill', 'fit', 'scale', 'thumb' (default: 'fill')
 * @param {string} [options.gravity] - Focus point: 'auto', 'center', 'face' (default: 'auto')
 * @param {string} [options.quality] - Quality: 'auto', 'auto:low', 'auto:eco', 'auto:good', 'auto:best'
 * @param {string} [options.format]  - Format: 'auto', 'webp', 'jpg' (default: 'auto')
 * @param {number} [options.dpr]     - Device pixel ratio (default: auto)
 * @param {string[]} [options.effects] - Cloudinary effect tokens WITHOUT the
 *        `e_` prefix, e.g. ["improve:outdoor:40", "vibrance:30"]. Applied
 *        after the resize so the effect operates on the delivered pixels
 *        rather than the master, which is both cheaper and what you want for
 *        sharpening.
 * @returns {string} - Optimized URL
 */
export const optimizeCloudinaryUrl = (url, options = {}) => {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("res.cloudinary.com")) return url;

  const {
    width,
    height,
    crop = "fill",
    gravity = "auto",
    quality = "auto",
    format = "auto",
    dpr,
    background,
    effects,
  } = options;

  // Build transformation parts
  const parts = [`f_${format}`, `q_${quality}`];

  if (width) parts.push(`w_${width}`);
  if (height) parts.push(`h_${height}`);
  // 53. c_xxx must come before g_xxx in some Cloudinary versions for reliability
  if (crop) parts.push(`c_${crop}`);

  // 54. Only include gravity for crop modes that support it (fit/scale/limit do NOT)
  const gravitySupported = [
    "fill",
    "lfill",
    "fill_pad",
    "thumb",
    "crop",
  ].includes(crop);
  if (gravity && gravitySupported) {
    parts.push(`g_${gravity}`);
  }

  // 55. c_pad requires a background to control the padding colour
  //     (e.g. background: "white" -> b_white). Without this, Cloudinary
  //     pads with black by default.
  if (background) parts.push(`b_${background}`);

  if (dpr) parts.push(`dpr_${typeof dpr === "number" ? dpr.toFixed(1) : dpr}`);

  // 56. Effects go LAST. Cloudinary applies a single-component transform in
  //     written order, so putting e_sharpen before the resize sharpens the
  //     master and then throws most of it away in the downscale.
  if (Array.isArray(effects)) {
    effects.filter(Boolean).forEach((effect) => parts.push(`e_${effect}`));
  }

  const transforms = parts.join(",");

  // Insert transforms between /upload/ and version path
  if (CLOUDINARY_UPLOAD_RE.test(url)) {
    return url.replace(CLOUDINARY_UPLOAD_RE, `/upload/${transforms}/$1`);
  }

  // Fallback: insert after /upload/
  const idx = url.indexOf("/upload/");
  if (idx !== -1) {
    const before = url.substring(0, idx + 8);
    const after = url.substring(idx + 8);
    return `${before}${transforms}/${after}`;
  }

  return url;
};

/**
 * Build width-descriptor candidates from one untouched Cloudinary master.
 * The browser selects the smallest useful candidate for its viewport and DPR.
 * `c_limit` callers can safely include the source width without upscaling.
 */
export const getResponsiveCloudinarySrcSet = (url, widths, options = {}) => {
  if (
    !url ||
    typeof url !== "string" ||
    !url.includes("res.cloudinary.com") ||
    !Array.isArray(widths)
  ) {
    return "";
  }

  const originalUrl = getOriginalCloudinaryUrl(url);
  const { aspectRatio, ...baseOptions } = options;

  return [...new Set(widths)]
    .filter((width) => Number.isFinite(width) && width > 0)
    .sort((a, b) => a - b)
    .map((width) => {
      const height = aspectRatio ? Math.round(width / aspectRatio) : undefined;
      const candidate = optimizeCloudinaryUrl(originalUrl, {
        ...baseOptions,
        width,
        ...(height ? { height } : {}),
      });
      return `${candidate} ${width}w`;
    })
    .join(", ");
};

/**
 * Preset configurations matching the platform's standard image contexts.
 * These align with the eager transforms set during upload.
 */
export const ImagePresets = {
  /** Restaurant/vendor banner cards (hero images) */
  heroBanner: { width: 1200, height: 800, quality: "auto:good" },

  /** Product cards in grids and lists */
  productCard: { width: 600, height: 400, quality: "auto" },

  /**
   * Product cards for vendor-shot ShopMedia images. Uses c_pad with a
   * white canvas so the full product is always visible (no cropping),
   * matching the server-side canonical master (c_pad,b_white,2000x2000).
   * Use this instead of `productCard` anywhere you render ShopMedia.
   */
  productPad: {
    width: 600,
    height: 600,
    crop: "pad",
    background: "white",
    quality: "auto:good",
  },

  /** Large product hero using white padding (no crop) */
  productHeroPad: {
    width: 1000,
    height: 1000,
    crop: "pad",
    background: "white",
    quality: "auto:good",
  },

  /** Small thumbnails (search results, cart items) */
  thumbnail: { width: 300, height: 300, quality: "auto:eco" },

  /** Restaurant listing cards on landing page */
  restaurantCard: { width: 600, height: 400, quality: "auto" },

  /** Brand carousel cards (banner/wide image — fills 16:9 zone) */
  brandCarousel: { width: 640, height: 360, quality: "auto:best" },

  /** Brand carousel cards on mobile (banner mode — 16:9) */
  brandCarouselMobile: { width: 480, height: 270, quality: "auto:good" },

  /** Brand carousel logo-only mode — square canvas, pad to preserve full logo, no crop */
  brandCarouselLogo: {
    width: 400,
    height: 400,
    crop: "pad",
    background: "white",
    quality: "auto:best",
  },

  /** Brand carousel logo-only mode on mobile — square canvas */
  brandCarouselLogoMobile: {
    width: 260,
    height: 260,
    crop: "pad",
    background: "white",
    quality: "auto:good",
  },

  /** Full product detail page hero */
  productHero: { width: 800, height: 800, crop: "fill", quality: "auto:good" },

  /** Full-image lightbox AND the zoom stages / magnifiers: c_limit scales
        DOWN only and never crops, so every edge of the photo survives
        whatever its aspect ratio. 1200 on the long edge is the LARGEST
        version any page may hand the browser: it is 2x a lightbox drawn at
        84vh on a laptop and covers a 2.5x zoom pane on a 480px stage, while
        never being the print-quality master (canonical 2000px). Raw
        `secure_url`s must not reach an <img>; see components/ImageGuard.jsx. */
  fullView: { width: 1200, crop: "limit", quality: "auto:good" },

  /** Thumbnail rails beside a product's main image (64 to 80px squares,
        requested at 2x). c_pad on white so a bottle or packet is never
        sliced by the square; auto:eco because it is 80px on screen. */
  railThumb: {
    width: 160,
    height: 160,
    crop: "pad",
    background: "white",
    quality: "auto:eco",
  },

  /** Cart item thumbnails */
  cartItem: { width: 200, height: 200, quality: "auto:eco" },

  /**
   * Retailer / promoted-vendor hero banner (card top image).
   * Canonical slot: 600×440 px — landscape fill, smart gravity so the
   * subject stays centred regardless of the uploaded image's orientation.
   * Doubles to @2x for retina: request 600 w, render at 300 CSS px.
   */
  vendorHero: {
    width: 600,
    height: 440,
    crop: "fill",
    gravity: "auto",
    quality: "auto:good",
  },

  /**
   * The promoted-vendor card's main image. `c_pad` with `b_auto`, and the
   * reason is that this one frame has to hold two different KINDS of asset.
   *
   * Vendors upload one image and it is either a product photograph or a
   * logo. Measured on the live promoted set: the photographs land at
   * 1.39-1.43:1, so they sit almost exactly on this frame and pad by a few
   * pixels. The logos are near square (Shopperz Bliss is 2000x1898, 1.05:1),
   * and `c_fill` was taking roughly a quarter off the top and bottom — its
   * circular mark and phone numbers were being sliced off. No gravity can
   * fix that: there is nothing in a logo that may be cropped.
   *
   * So nothing is cropped. `b_auto` fills the remainder with the image's own
   * predominant colour, which makes the padding read as the card's design
   * rather than as letterboxing, and every card comes out the same shape.
   *
   * Do NOT point this at `bannerUrl` / `shopBannerUrl`. Those are the SHOP
   * PAGE's 1440x175 strip (8.2:1) — a different asset class entirely, and
   * `c_fill`-ing one into this frame slices the middle out of it. The field
   * meant for this is `cardImageUrl` (1280x720), which is 16:9 and pads to
   * almost nothing here.
   */
  vendorCard: {
    width: 720,
    height: 528,
    crop: "pad",
    background: "auto",
    quality: "auto:good",
  },

  /**
   * Retailer / promoted-vendor avatar thumbnail (52×52 CSS px card overlay).
   * Square fill with smart gravity so product/logo subject is never cut.
   */
  vendorAvatar: {
    width: 200,
    height: 200,
    crop: "fill",
    gravity: "auto",
    quality: "auto:eco",
  },

  /**
   * Vendor / restaurant LOGO tile on a shop-page banner (~90 CSS px, so
   * 240 covers @2x). c_fit scales down inside the box and never crops or
   * pads, which is what a logo needs: a wide wordmark keeps its shape and
   * a transparent background stays transparent. Do NOT use vendorAvatar
   * here: its c_fill crops the sides off anything that isn't square.
   */
  vendorLogo: { width: 240, height: 240, crop: "fit", quality: "auto:good" },

  /**
   * The same logo, on a LISTING card rather than a banner badge. The card's
   * image frame is about 234x160 CSS px inside its padding, so 600x440
   * covers it at 2x with room for the single-column mobile layout too.
   * `c_fit` again, and for the same reason: `restaurantCard` (c_fill 3:2)
   * was cropping the top and bottom off square logos BEFORE the browser saw
   * them, then `object-fit: cover` cropped what was left a second time.
   */
  vendorLogoCard: {
    width: 600,
    height: 440,
    crop: "fit",
    quality: "auto:good",
  },

  /**
   * The hero DISH on a listing card. 900x540 is exactly 5:3, which is the
   * aspect the card's media frame declares, so Cloudinary's g_auto makes the
   * one crop and the browser's `object-fit: cover` has nothing left to take.
   * A food photo is the one thing on these cards that SHOULD be cropped to
   * fill: unlike a logo, a plate has no edges that must survive, and
   * g_auto keeps the food itself centred.
   */
  cardHero: {
    width: 900,
    height: 540,
    crop: "fill",
    gravity: "auto",
    quality: "auto:good",
  },

  /**
   * The dish on a restaurant card inside the landing page's app showcase
   * (AppDownloadShowcase). The card is drawn at phone size and then scaled
   * down, so it is never wider than about 200 CSS px on screen: 400x250
   * covers that at 2x, is the card's own 1.6 aspect, and is a quarter of
   * the bytes of `cardHero`. Same crop rules as `cardHero`.
   */
  appShowcaseDish: {
    width: 400,
    height: 250,
    crop: "fill",
    gravity: "auto",
    quality: "auto:eco",
  },

  /**
   * The SHARE CARD — the 1200x630 slot WhatsApp, Facebook, Slack, Telegram and
   * X all render a link preview into.
   *
   * This preset exists so the in-app share sheet can show the card the crawler
   * will actually build, rather than an impression of it. Its parts are
   * therefore not free choices: they emit exactly
   * `f_jpg,q_auto:good,w_1200,h_630,c_pad,b_white`, which is the
   * OG_CARD_TRANSFORM string in netlify/edge-functions/og-inject.js. **Change
   * one and change the other**, or the preview starts lying.
   *
   * Why each part is what it is:
   *   c_pad + b_white — vendors upload LOGOS (Cakely's is 588x558). c_fill
   *     would crop a near-square mark to 1.91:1 and lose its top and bottom;
   *     c_pad never crops, so the declared 1200x630 becomes true rather than
   *     being a claim the image does not satisfy.
   *   f_jpg, not f_auto — f_auto can hand a crawler webp, which several of
   *     them will not render at all, and a padded canvas needs an opaque
   *     format anyway.
   */
  ogShareCard: {
    width: 1200,
    height: 630,
    crop: "pad",
    background: "white",
    format: "jpg",
    quality: "auto:good",
  },
  /**
   * The share card for a PHOTOGRAPH (a restaurant's card image or cover
   * dish): fills the 1200x630 frame instead of sitting between white bars.
   * Emits exactly `f_jpg,q_auto:good,w_1200,h_630,c_fill,g_auto`, which is
   * OG_CARD_TRANSFORM_COVER in og-inject.js. Same rule as above: change one
   * and change the other.
   */
  ogShareCardCover: {
    width: 1200,
    height: 630,
    crop: "fill",
    gravity: "auto",
    format: "jpg",
    quality: "auto:good",
  },
};

/**
 * Convenience: get an optimized URL using a named preset.
 *
 * @param {string} url    - Raw Cloudinary URL
 * @param {string} preset - Preset name from ImagePresets
 * @returns {string}
 *
 * @example
 * getOptimizedUrl(restaurant.imageUrl, 'restaurantCard')
 * getOptimizedUrl(product.product_image, 'productCard')
 * getOptimizedUrl(item.imageUrl, 'cartItem')
 */
export const getOptimizedUrl = (url, preset = "productCard") => {
  const config = ImagePresets[preset] || ImagePresets.productCard;
  return optimizeCloudinaryUrl(url, config);
};

export default optimizeCloudinaryUrl;
